"use client"
import { Trans } from "@lingui/react/macro"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { type ActiveDotProps, ActiveDotReatom } from "./active-dot.reatom"
import { FloorPriceChartMoleculeReatom } from "./floor-price-chart-molecule.reatom"
import { DateLabel, FloorLabel, PriceLabel } from "./labels"
import { useFloorPriceChartReatom } from "./use-floor-price-chart.reatom"

/**
 * Used to calculate padding for the axis to avoid overflow of the chart
 * Approximate values are used instead of exact dimensions to avoid extra computations and complexity
 *
 * In case styles or format of the labels changes, these values should be updated
 */
const APROXIMATE_PRICE_LABEL_WIDTH = 170
const APROXIMATE_DATE_LABEL_HEIGHT = 30
const DEFAULT_PADDING = 5

const CHART_SETTINGS = {
  // Overflow visible is used to show labels even if they are outside the chart
  overflow: "visible",
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
}

/**
 * Composition of ReferenceLine in any component is not working properly, so we use this object to pass common props
 *
 * @see https://github.com/recharts/recharts/issues/544
 */
const REF_LINE_PROPS = {
  stroke: "white",
  strokeOpacity: 0.2,
  strokeWidth: 1,
  strokeDasharray: "2 2",
}

export const FloorPriceChartReatom = reatomComponent(() => {
  const molecule = useMolecule(FloorPriceChartMoleculeReatom)
  const {
    isLoading,
    data,
    chartData,
    hasEnoughData,
    floorPrice,
    minPrice,
    maxPrice,
    trendColor,
    dateFormatter,
    approximateDateLabelWidth,
  } = useFloorPriceChartReatom()

  const activeDot = molecule.activeDot()

  if (isLoading || !data) return null

  if (!hasEnoughData) {
    return (
      <div className="text-foreground/60 text-lg">
        <Trans>Oops! No data to display</Trans>
      </div>
    )
  }

  const { currency } = data

  return (
    <ResponsiveContainer>
      <LineChart {...CHART_SETTINGS} data={chartData}>
        <XAxis
          padding={{
            left: DEFAULT_PADDING,
            right: APROXIMATE_PRICE_LABEL_WIDTH / 2,
          }}
          dataKey="date"
          tickFormatter={dateFormatter}
          interval="preserveStartEnd"
          minTickGap={100}
        />
        <YAxis
          padding={{
            top: DEFAULT_PADDING,
            bottom: APROXIMATE_DATE_LABEL_HEIGHT,
          }}
          hide
          dataKey="value"
          domain={["dataMin", "dataMax"]}
        />
        <Tooltip content={TooltipContent} cursor={false} />
        <Line
          isAnimationActive={true}
          animationDuration={200}
          animationEasing="ease-in-out"
          strokeWidth={2}
          dataKey="value"
          dot={false}
          activeDot={(props: ActiveDotProps) => <ActiveDotReatom {...props} />}
          stroke={trendColor}
          connectNulls={true}
        />
        {minPrice !== null && (
          <ReferenceLine
            {...REF_LINE_PROPS}
            y={minPrice}
            label={(props) => (
              <PriceLabel
                value={minPrice}
                position={{ x: props.viewBox.width, y: props.viewBox.y }}
                currency={currency}
                className="bg-background [&_span]:text-white"
              />
            )}
          />
        )}
        {maxPrice !== null && (
          <ReferenceLine
            {...REF_LINE_PROPS}
            y={maxPrice}
            label={(props) => (
              <PriceLabel
                value={maxPrice}
                position={{ x: props.viewBox.width, y: props.viewBox.y }}
                currency={currency}
                className="bg-background [&_span]:text-white"
              />
            )}
          />
        )}
        {floorPrice !== null && (
          <ReferenceLine
            {...REF_LINE_PROPS}
            y={floorPrice}
            label={(props) => (
              <FloorLabel
                value={floorPrice}
                position={{ x: props.viewBox.width, y: props.viewBox.y }}
                currency={currency}
              />
            )}
          />
        )}
        {activeDot !== undefined && (
          <ReferenceLine
            {...REF_LINE_PROPS}
            x={activeDot.date}
            label={(props) => (
              <DateLabel
                position={{ x: props.viewBox.x, y: props.viewBox.height }}
                value={dateFormatter(activeDot.date)}
                approximateWidth={approximateDateLabelWidth}
              />
            )}
          />
        )}
        {activeDot !== undefined && (
          <ReferenceLine
            {...REF_LINE_PROPS}
            y={activeDot.value}
            label={(props) => (
              <PriceLabel
                value={activeDot.value}
                position={{ x: props.viewBox.width, y: props.viewBox.y }}
                currency={currency}
              />
            )}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  )
})

// Tooltip is empty for regular dots
function TooltipContent() {
  return null
}
